import ExcelJS from "exceljs";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const COLUMN_DEFS = [
  { header: "Employee ID",   key: "employeeId",     width: 16 },
  { header: "Associate",     key: "associateName",  width: 26 },
  { header: "MAC Code",      key: "macCode",        width: 14 },
  { header: "SOW Code",      key: "sowCode",        width: 16 },
  { header: "SOW Name",      key: "sowName",        width: 30 },
  { header: "Position Type", key: "positionType",   width: 16 },
  { header: "Allocated FTE (Prorated)",  key: "fte",           width: 18 },
  { header: "Start Date",    key: "startDate",      width: 14 },
  { header: "End Date",      key: "endDate",        width: 14 },
  { header: "Status",                    key: "status",                  width: 14 },
  { header: "Balance Cost Attributed To", key: "balanceCostAttributedTo", width: 22 },
];

const STATUS_STYLES = {
  ACTIVE:     { bg: "E1F5EE", fg: "085041" },
  ENDED:      { bg: "FAFADE", fg: "6B7300" },
  SUPERSEDED: { bg: "FCEBEB", fg: "791F1F" },
};

const FTE_STYLES = {
  "1":    { bg: "E1F5EE", fg: "085041" },
  "0.75": { bg: "FAFADE", fg: "6B7300" },
  "0.5":  { bg: "E6F1FB", fg: "0C447C" },
  "0.25": { bg: "FCEBEB", fg: "791F1F" },
};

const TEAL_HDR    = "28B3B4";
const TEAL_HDR2   = "1A8E8F";
const TEAL_TEXT   = "085041";
const ROW_EVEN_BG = "F0FAFA";
const ROW_ODD_BG  = "FFFFFF";
const BORDER_CLR  = "C8E8E6";

function hairBorder(color = BORDER_CLR) {
  const s = { style: "hair", color: { argb: "FF" + color } };
  return { top: s, bottom: s, left: s, right: s };
}

function mediumBorder(color = TEAL_HDR2) {
  const m = { style: "medium", color: { argb: "FF" + color } };
  const t = { style: "thin",   color: { argb: "FF" + color } };
  return { top: m, bottom: m, left: t, right: t };
}

export async function exportReport({
  year,
  month,
  rows = [],
  filters = {},
  macs = [],
  associates = [],
}) {

  // ── 1. Filter ────────────────────────────────────────────────────────────
  // Rows are already filtered by the backend (mac, associate, status, fte).
  // Only apply client-side filters that the backend doesn't handle (none currently).
  const filtered = rows;

  // ── 2. Build workbook ────────────────────────────────────────────────────
  const wb = new ExcelJS.Workbook();
  wb.creator  = "Psi360";
  wb.created  = new Date();

  const sheetName = `${MONTH_NAMES[month - 1]} ${year}`;
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 1 }],  // freeze header
  });

  // ── 3. Columns ───────────────────────────────────────────────────────────
  ws.columns = COLUMN_DEFS.map((c) => ({
    header: c.header,
    key:    c.key,
    width:  c.width,
  }));

  // ── 4. Style header row ──────────────────────────────────────────────────
  const headerRow = ws.getRow(1);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.font      = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Arial" };
    cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + TEAL_HDR } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border    = mediumBorder();
  });

  // ── 5. AutoFilter on header ──────────────────────────────────────────────
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to:   { row: 1, column: COLUMN_DEFS.length },
  };

  // ── 6. Data rows ─────────────────────────────────────────────────────────
  const dataToAdd = filtered.length > 0 ? filtered : [{}];

  dataToAdd.forEach((rowData, idx) => {
    const excelRowIdx = idx + 2; // 1-indexed, row 1 is header
    const isEven      = excelRowIdx % 2 === 0;
    const defaultBg   = isEven ? ROW_EVEN_BG : ROW_ODD_BG;

    const row = ws.addRow(
      COLUMN_DEFS.reduce((acc, col) => {
        acc[col.key] = rowData[col.key] ?? "";
        return acc;
      }, {})
    );
    row.height = 18;

    COLUMN_DEFS.forEach((col, colIdx) => {
      const cell  = row.getCell(colIdx + 1);
      const value = rowData[col.key];

      let bg = defaultBg;
      let fg = TEAL_TEXT;
      let bold = false;
      let align = "left";

      if (col.key === "status" && STATUS_STYLES[value]) {
        bg   = STATUS_STYLES[value].bg;
        fg   = STATUS_STYLES[value].fg;
        bold = true;
        align = "center";
      } else if (col.key === "fte" && FTE_STYLES[String(value)]) {
        bg   = FTE_STYLES[String(value)].bg;
        fg   = FTE_STYLES[String(value)].fg;
        bold = true;
        align = "center";
      }

      cell.font      = { name: "Arial", size: 10, color: { argb: "FF" + fg }, bold };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + bg } };
      cell.alignment = { horizontal: align, vertical: "middle" };
      cell.border    = hairBorder();
    });
  });

  // ── 7. Download ──────────────────────────────────────────────────────────
  const buffer   = await wb.xlsx.writeBuffer();
  const blob     = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url      = URL.createObjectURL(blob);
  const a        = document.createElement("a");
  a.href         = url;
  a.download     = `report_${year}_${String(month).padStart(2, "0")}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}